import React from 'react';
import { Note } from '../Note/Note';
import { Expander } from '../Expander/Expander';
import { DocumentationTypo } from './DocumentationTypo';
import { DevPreviewBanner } from './DevPreviewBanner';

const { Paragraph, H1, H2, H3, Anchor, CodeBlock, CodeBlockInline, OrderedList, UnorderedList, DocPage } =
    DocumentationTypo;

const displayDevPreviewWarnings = __appConfig.app.displayDevPreviewWarnings;

export const GrammarDocPage: React.FC = () => {
    return (
        <DocPage>
            <H1>Обработка естественного языка (NLP)</H1>
            {displayDevPreviewWarnings && <DevPreviewBanner />}
            <Paragraph>
                Чтобы с&nbsp;навыком можно было общаться на&nbsp;естественном языке, он&nbsp;должен уметь корректно
                обрабатывать реплики пользователей&nbsp;&mdash; распознавать задачу, которую хочет решить пользователь
                (например, заказать такси), а&nbsp;также извлекать нужные детали (на&nbsp;какое число
                и&nbsp;по&nbsp;какому адресу).
            </Paragraph>
            <Paragraph>
                Для упрощения задач NLP (Natural Language Processing) Диалоги предоставляют специальный
                инструмент&nbsp;&mdash; встроенный язык для описания пользовательского запроса. С&nbsp;его помощью
                вы&nbsp;можете описать правила, по&nbsp;которым Диалоги будут классифицировать запросы и&nbsp;извлекать
                из&nbsp;них нужные данные (эти правила вы&nbsp;будете задавать в&nbsp;консоли разработчика).
            </Paragraph>
            <Paragraph>
                Когда пользователь произнесет команду, Диалоги распознают текст и&nbsp;извлекут те&nbsp;фразы, которые
                описывают намерения пользователя согласно вашим правилам. Распознанные данные Диалоги пришлют
                в&nbsp;навык.
            </Paragraph>

            <article>
                <H2>Что такое интент, форма и&nbsp;слоты</H2>
                <Paragraph>Каждую реплику можно разбить на&nbsp;компоненты: интент, форма и&nbsp;слоты.</Paragraph>
                <Paragraph>
                    <em>Интент</em>&nbsp;&mdash; это задача, которую пользователь формулирует в&nbsp;конкретной реплике.
                    Например, <CodeBlockInline>узнать погоду</CodeBlockInline>. Каждому интенту соответствует одна
                    форма.
                </Paragraph>
                <Paragraph>
                    <em>Форма</em>&nbsp;&mdash; контейнер с&nbsp;информацией, который Диалоги заполняют, распознавая
                    запрос пользователя. Форма всегда соответствует одному интенту и&nbsp;содержит набор типизированных
                    слотов.
                </Paragraph>
                <Paragraph>
                    <em>Слот</em>&nbsp;&mdash; поле формы. Каждый слот имеет название, тип данных и&nbsp;признак
                    обязательности. Например, для запроса{' '}
                    <CodeBlockInline>погода на&nbsp;завтра в&nbsp;Питере</CodeBlockInline> будут слоты{' '}
                    <CodeBlockInline>завтра</CodeBlockInline> и&nbsp;
                    <CodeBlockInline>в&nbsp;Питере</CodeBlockInline>.
                </Paragraph>
                <Paragraph>
                    При обработке реплики Диалоги сначала определяют, к&nbsp;какому интенту она относится. После этого
                    извлекают из&nbsp;реплики необходимые параметры и&nbsp;заполняют ими слоты интента. Распознанные
                    данные Диалоги отправят в&nbsp;навык в&nbsp;поле{' '}
                    <CodeBlockInline>request.nlu.intents</CodeBlockInline>. Если реплика не&nbsp;относится
                    ни&nbsp;к&nbsp;одному интенту, поле <CodeBlockInline>request.nlu.intents</CodeBlockInline> будет
                    пустым. <Anchor to="data-to-skill">Подробнее</Anchor>
                </Paragraph>
            </article>
            <article>
                <H2>Как создать правила для обработки реплик</H2>
                <OrderedList>
                    <li>
                        Откройте консоль разработчика. На&nbsp;странице навыка Алисы перейдите во&nbsp;вкладку{' '}
                        <strong>Настройки</strong> и&nbsp;выберите раздел <strong>Интенты</strong>.
                    </li>
                    <li>
                        Создайте форму интента. Эта форма будет однозначно определять интент&nbsp;&mdash; содержать его
                        название и&nbsp;грамматику, а&nbsp;также указывать набор слотов.
                    </li>
                    <li>
                        Зайдите в&nbsp;сохраненную форму. Заполните поля:
                        <UnorderedList>
                            <li>
                                <strong>Название</strong>&nbsp;&mdash; человекочитаемый текст, который будет
                                отображаться в&nbsp;списке форм.
                            </li>
                            <li>
                                <strong>ID</strong>&mdash; идентификатор интента. Будет указан в&nbsp;JSON, который
                                Диалоги <Anchor to="data-to-skill">отправят</Anchor> навыку.
                            </li>
                            <li>
                                <strong>Грамматика</strong>&nbsp;&mdash; описание интента согласно{' '}
                                <Anchor to="granet-sntx">синтаксису</Anchor>.
                            </li>
                        </UnorderedList>
                    </li>
                    <li>
                        Протестируйте грамматику. Для этого укажите одну или несколько тестовых фраз в&nbsp;полях{' '}
                        <strong>Положительные тесты</strong> или <strong>Отрицательные тесты</strong>. Для положительных
                        тестов будет проверяться, что фраза классифицируется текущим интентом. Для отрицательных,
                        наоборот. Далее, нажмите кнопку <strong>Протестировать</strong>. Результат отобразится в поле{' '}
                        <strong>Результаты тестирования</strong>, либо, если указана некорректная грамматика, ошибка
                        подсветится в&nbsp;редакторе.
                    </li>
                    <li>
                        Если грамматика срабатывает корректно, нажмите Сохранить. После этого, на вкладке тестирования,
                        Диалоги начнут отправлять данные в&nbsp;навык согласно{' '}
                        <Anchor to="data-to-skill">протоколу</Anchor>.
                    </li>
                    <li>
                        Теперь опубликуйте или переопубликуйте навык для того, чтобы интенты стали доступны. При
                        изменении настроек интентов, навык также нужно будет переопубликовать.
                    </li>
                </OrderedList>
            </article>

            <article>
                <H2 id="granet-sntx">Синтаксис</H2>
                <section>
                    <Paragraph>Ниже показан пример описания интента:</Paragraph>
                    <Paragraph>
                        <CodeBlock type="granet">
                            {`# Описание интента "turn.on" для включения устройств.
# Эта грамматика позволит распознавать фразы, например, "включи свет на кухне"
# или "включи кондиционер в спальне".

# Корневой элемент грамматики. Описывает шаблон, по которому будет
# отбираться реплика.
root:
    включи $What $Where
# Описание слотов. Диалоги будут отправлять это описание навыку.
slots:
    what:
        source: $What
    where:
        source: $Where
$What:
    свет | кондиционер
$Where:
    в ванной | на кухне | в спальне`}
                        </CodeBlock>
                    </Paragraph>
                    <Paragraph>
                        <Note
                            type="info"
                            inlineContent={
                                <>Вложенные элементы обозначаются при помощи отступа в&nbsp;4&nbsp;пробела.</>
                            }
                        />
                    </Paragraph>
                    <Paragraph>
                        Описание интента состоит из&nbsp;ключевых слов <CodeBlockInline>root</CodeBlockInline>,{' '}
                        <CodeBlockInline>slots</CodeBlockInline> и&nbsp;
                        <CodeBlockInline>filler</CodeBlockInline>, а&nbsp;также нетерминалов&nbsp;&mdash; фраз
                        на&nbsp;естественном языке, описывающих, на&nbsp;какие запросы должна срабатывать грамматика.
                        Нетерминалы обозначаются символом&nbsp;
                        <CodeBlockInline>$</CodeBlockInline>. Они эквиваленты переменным в&nbsp;языках программирования.
                    </Paragraph>
                    <Paragraph>Ниже рассмотрено каждое из&nbsp;ключевых слов:</Paragraph>
                    <UnorderedList>
                        <li>
                            <Paragraph>
                                <CodeBlockInline>root</CodeBlockInline> — обозначение корневого элемента. Описывает
                                шаблон, по которому будет отбираться вся реплика целиком.{' '}
                                <CodeBlockInline>root</CodeBlockInline>&nbsp;&mdash; обозначение корневого элемента.
                                Описывает шаблон, по&nbsp;которому будет отбираться вся реплика целиком.
                            </Paragraph>
                            <Expander title="Пример" type="doc">
                                <Paragraph>
                                    <CodeBlock type="granet">
                                        {`root:
    [включи $What $Where (и $Where)*]`}
                                    </CodeBlock>
                                </Paragraph>
                                <Paragraph>
                                    В&nbsp;этом примере используются <Anchor to="nlu-quantifier">квантификатор</Anchor>{' '}
                                    и&nbsp;
                                    <Anchor to="nlu-words-bag">оператор [ ]</Anchor>.
                                </Paragraph>
                            </Expander>
                        </li>
                        <li>
                            <Paragraph>
                                <CodeBlockInline>slots</CodeBlockInline>&nbsp;&mdash; описание слотов запроса. Это поле
                                будет присутствовать в&nbsp;JSON, который Диалоги отправят в&nbsp;навык после обработки
                                запроса. Подробнее см.{' '}
                                <Anchor to="data-to-skill">Какие данные передаются в&nbsp;навык</Anchor>.
                            </Paragraph>
                            <Expander title="Пример" type="doc">
                                <Paragraph>
                                    <CodeBlock type="granet">
                                        {`slots:
    what:
        source: $What
    where:
        source: $Where
$What:
    свет | кондиционер
$Where:
    # Подойдет любая строка, которую введет пользователь.
    .+
    `}
                                    </CodeBlock>
                                </Paragraph>
                            </Expander>
                        </li>
                        <li>
                            <Paragraph>
                                <CodeBlockInline>filler</CodeBlockInline>&nbsp;&mdash; стоп-слова, которые можно
                                отбросить при разборе запроса. Для исключения незначащих, неинформативных слов
                                используется специальный классификатор, использующий контекст предложения. Например, для
                                разбора из примера выше срабаботает как фраза &quot;включи свет&quot;, так и
                                &quot;включи свет, пожалуйста&quot;.
                            </Paragraph>
                            <Expander title="Пример" type="doc">
                                <Paragraph>
                                    <CodeBlock type="granet">
                                        {`filler:
    мне | как всегда | ещё раз | нужно`}
                                    </CodeBlock>
                                </Paragraph>
                            </Expander>
                        </li>
                    </UnorderedList>
                    <Paragraph>
                        <Note
                            type="info"
                            inlineContent={<>Порядок описания элементов в&nbsp;грамматике не&nbsp;имеет значения.</>}
                        />
                    </Paragraph>
                </section>
                <section>
                    <H3>Типизированные слоты</H3>
                    <Paragraph>
                        Слоты могут содержать не&nbsp;только строковое значение, но&nbsp;и&nbsp;именованные сущности:
                        <UnorderedList>
                            <li>
                                <CodeBlockInline>YANDEX.NUMBER</CodeBlockInline> - числа
                            </li>
                            <li>
                                <CodeBlockInline>YANDEX.FIO</CodeBlockInline> - имена
                            </li>
                            <li>
                                <CodeBlockInline>YANDEX.DATETIME</CodeBlockInline> - даты
                            </li>
                            <li>
                                <CodeBlockInline>YANDEX.GEO</CodeBlockInline> - гео-объекты
                            </li>
                        </UnorderedList>
                    </Paragraph>
                    <Paragraph>
                        Для указания типизированного слота используются поля <CodeBlockInline>type</CodeBlockInline>{' '}
                        и&nbsp;нетерминал, содержащий этот тип:
                    </Paragraph>
                    <Paragraph>
                        <CodeBlock type="granet">
                            {`slots:
    from:
        source: $From
        type: YANDEX.NUMBER
    to:
        source: $To
        type: YANDEX.NUMBER
root:
    назови число от $From до $To
$From:
    $YANDEX.NUMBER
$To:
    $YANDEX.NUMBER`}
                        </CodeBlock>
                    </Paragraph>
                </section>
                <section>
                    <H3>Пользовательские сущности в слотах</H3>
                    <Paragraph>
                        Чтобы задать один или несколько пользовательских типов слотов, ошишите их в разделе
                        &quot;Сущности&quot;, например:
                    </Paragraph>
                    <Paragraph>
                        <CodeBlock type="granet">
                            {`entity ChessPiece:
    values:
        queen:
            ферзь
            королева
        pawn:
            пешка`}
                        </CodeBlock>
                    </Paragraph>
                    <Paragraph>
                        После этого тип станет доступен в качестве нетерминала грамматики и типа слота:
                    </Paragraph>
                    <Paragraph>
                        <CodeBlock type="granet">
                            {`slots:
    piece:
        type: ChessPiece
        source: $Piece
root:
    ход $Piece
$Piece:
    $ChessPiece`}
                        </CodeBlock>
                    </Paragraph>
                </section>
                <section>
                    <H3>Директивы</H3>
                    <Paragraph>
                        Директива&nbsp;&mdash; это специальная команда, переключающая парсер запросов
                        в&nbsp;определенный режим работы. Директивы всегда начинаются с&nbsp;символа&nbsp;
                        <CodeBlockInline>%</CodeBlockInline>. Например:
                    </Paragraph>
                    <Paragraph>
                        <CodeBlock type="granet">
                            {`# Все последующие нетерминалы будут сравниваться без учета формы слова.
# Сработает как "включи свет", так и "включай свет".
root:
    %lemma
    включи свет`}
                        </CodeBlock>
                    </Paragraph>
                    <Paragraph>
                        Действие директивы распространяется на&nbsp;все последующие нетерминалы. Директиву можно указать
                        в&nbsp;начале всей грамматики, а&nbsp;также непосредственно перед нетерминалом. В&nbsp;последнем
                        случае директива будет действовать до&nbsp;конца отступа или до&nbsp;отменяющей директивы.
                        Пример:
                    </Paragraph>
                    <Paragraph>
                        <CodeBlock type="granet">
                            {`filler:
    # Директива %lemma действует до %exact. Формы слов не учитываются.
    %lemma
    большое спасибо
    # Отменяем действие %lemma. Сработает только "всегда пожалуйста".
    %exact
    всегда пожалуйста`}
                        </CodeBlock>
                    </Paragraph>
                    <Paragraph>
                        <Expander title="%lemma" type="doc">
                            <Paragraph>Нетерминалы будут сравниваться без учета формы слова. Пример:</Paragraph>
                            <Paragraph>
                                <CodeBlock type="granet">
                                    {`
root:
    %lemma
    включи свет`}
                                </CodeBlock>
                            </Paragraph>
                            <Paragraph>
                                Запросы <CodeBlockInline>включи свет</CodeBlockInline> и&nbsp;
                                <CodeBlockInline>включай свет</CodeBlockInline> будут засчитаны как совпадение.
                            </Paragraph>
                        </Expander>
                    </Paragraph>
                    <Paragraph>
                        <Expander title="%exact" type="doc">
                            <Paragraph>Нетерминалы будут сравниваться по&nbsp;точному совпадению. Пример:</Paragraph>
                            <Paragraph>
                                <CodeBlock type="granet">
                                    {`
root:
    %exact
    включи свет`}
                                </CodeBlock>
                            </Paragraph>
                            <Paragraph>
                                Под это правило попадет только запрос <CodeBlockInline>включи свет</CodeBlockInline>.
                            </Paragraph>
                        </Expander>
                    </Paragraph>
                </section>

                <section>
                    <H3 id="nlu-words-bag">Оператор [ ]</H3>
                    <Paragraph>Позволяет игнорировать порядок слов в&nbsp;грамматике. Пример:</Paragraph>
                    <Paragraph>
                        <CodeBlock type="granet">
                            {`root:
    [включи свет] `}
                        </CodeBlock>
                    </Paragraph>
                    <Paragraph>
                        В&nbsp;этом примере положительными срабатываниями будут{' '}
                        <CodeBlockInline>включи свет</CodeBlockInline> и&nbsp;
                        <CodeBlockInline>свет включи</CodeBlockInline>.
                    </Paragraph>
                </section>

                <section>
                    <H3 id="nlu-quantifier">Квантификаторы</H3>
                    <Paragraph>В&nbsp;описании элементов грамматики можно использовать квантификаторы:</Paragraph>
                    <UnorderedList>
                        <li>
                            <Paragraph>
                                <CodeBlockInline>?</CodeBlockInline> &mdash;&nbsp;одно или ноль вхождений;
                            </Paragraph>
                        </li>
                        <li>
                            <Paragraph>
                                <CodeBlockInline>*</CodeBlockInline> &mdash; ноль или больше вхождений;
                            </Paragraph>
                        </li>
                        <li>
                            <Paragraph>
                                <CodeBlockInline>+</CodeBlockInline> &mdash; хотя&nbsp;бы одно вхождение.
                            </Paragraph>
                        </li>
                    </UnorderedList>
                    <Paragraph>
                        <Expander title="Пример «включи свет»" type="doc">
                            <Paragraph>
                                <CodeBlock type="granet">
                                    {`root:
    включи (пожалуйста)? свет`}
                                </CodeBlock>
                            </Paragraph>
                            <Paragraph>
                                Положительными срабатываниями будет как <CodeBlockInline>включи свет</CodeBlockInline>,
                                так и&nbsp;
                                <CodeBlockInline>включи, пожалуйста, свет</CodeBlockInline>.
                            </Paragraph>
                        </Expander>
                    </Paragraph>
                    <Paragraph>
                        <Expander title="Пример «включи свет на кухне»" type="doc">
                            <Paragraph>
                                <CodeBlock type="granet">
                                    {`root:
    включи свет $Where (и $Where)*
$Where:
    .+ # Хотя бы одно произвольное слово.`}
                                </CodeBlock>
                            </Paragraph>
                            <Paragraph>
                                Положительными срабатываниями будут{' '}
                                <CodeBlockInline>включи свет на&nbsp;кухне</CodeBlockInline>,{' '}
                                <CodeBlockInline>включи свет на&nbsp;кухне и&nbsp;во&nbsp;всем доме</CodeBlockInline>{' '}
                                и&nbsp;т.&nbsp;д.
                            </Paragraph>
                        </Expander>
                    </Paragraph>
                    <Paragraph>
                        <Expander title="Пример «включи свет на кухне и в ванной»" type="doc">
                            <Paragraph>
                                <CodeBlock type="granet">
                                    {`root:
    включи свет $Where (и $Where)+
$Where:
    на кухне | в ванной | в коридоре`}
                                </CodeBlock>
                            </Paragraph>
                            <Paragraph>
                                Положительными срабатываниями будут{' '}
                                <CodeBlockInline>включи свет на&nbsp;кухне и&nbsp;в&nbsp;ванной</CodeBlockInline>,{' '}
                                <CodeBlockInline>
                                    включи свет на&nbsp;кухне и&nbsp;ванной, и&nbsp;в&nbsp;коридоре
                                </CodeBlockInline>{' '}
                                и&nbsp;т.&nbsp;д.
                            </Paragraph>
                        </Expander>
                    </Paragraph>
                    <Paragraph>
                        <Note
                            type="info"
                            inlineContent={
                                <>
                                    В&nbsp;слот попадет только первый распознанный нетерминал, если в&nbsp;одном интенте
                                    он&nbsp;используется несколько раз (только первый{' '}
                                    <CodeBlockInline>$Where</CodeBlockInline> из&nbsp;примеров выше попадет в&nbsp;слот)
                                </>
                            }
                        />
                    </Paragraph>
                </section>
            </article>
            <article>
                <H2 id="builtin-intents">Встроенные интенты</H2>
                <Paragraph>
                    Если в навыке есть хотя бы один интент, Яндекс Диалоги дополнительно отправляют интенты,
                    универсальные для большинства навыков.
                    <UnorderedList>
                        <li>
                            <CodeBlockInline>YANDEX.CONFIRM</CodeBlockInline>&nbsp;&mdash;&nbsp;согласие
                        </li>
                        <li>
                            <CodeBlockInline>YANDEX.REJECT</CodeBlockInline>&nbsp;&mdash;&nbsp;отказ
                        </li>
                        <li>
                            <CodeBlockInline>YANDEX.HELP</CodeBlockInline>&nbsp;&mdash;&nbsp;запрос подсказки
                        </li>
                        {/*
                        TODO: uncomment after this intent will be renamed from WHAT_YOU_CAN to WHAT_CAN_YOU_DO
                        <li>
                            <CodeBlockInline>YANDEX.WHAT_CAN_YOU_DO</CodeBlockInline>&nbsp;&mdash;&nbsp;запросы вида "Что ты умеешь?"
                        </li>
                        */}
                        <li>
                            <CodeBlockInline>YANDEX.REPEAT</CodeBlockInline>&nbsp;&mdash;&nbsp;просьба повторить
                            последний ответ навыка
                        </li>
                    </UnorderedList>
                </Paragraph>
            </article>
            <article>
                <H2 id="data-to-skill">Какие данные передаются в&nbsp;навык</H2>
                <Paragraph>
                    После того как запрос будет обработан, Диалоги отправят навыку распознанные данные&nbsp;&mdash;
                    в&nbsp;поле <CodeBlockInline>request.nlu.intents</CodeBlockInline>. Это поле содержит идентификатор
                    интента, а&nbsp;также описание заполненных слотов. Например:
                </Paragraph>
                <Paragraph>
                    <CodeBlock type="json">
                        {`"request": {
    "command": "включи свет на кухне, пожалуйста",
    "nlu": {
        "intents": {
            "turn.on": { // Интент.
                "slots": {  // Список слотов.
                    "what": {
                        "type": "YANDEX.STRING",
                        "value": "свет"
                    },
                    "where": {
                        "type": "YANDEX.STRING",
                        "value": "на кухне"
                    }
                }
            }
        }
    },
    ...
}`}
                    </CodeBlock>
                </Paragraph>
                <Paragraph>
                    Если реплика не&nbsp;относится ни&nbsp;к&nbsp;одному интенту, поле{' '}
                    <CodeBlockInline>request.nlu.intents</CodeBlockInline> будет пустым.
                </Paragraph>
                <Paragraph>
                    В случае типизированных слотов в запросе будет указан тип слота и его каноническое значение. Пример
                    для чисел:
                </Paragraph>
                <Paragraph>
                    <CodeBlock type="json">
                        {`"request": {
    "command": "назови число от одного до шести",
    "nlu": {
        "intents": {
            "random_number": { // Интент.
                "slots": {  // Список слотов.
                    "from": {
                        "type": "YANDEX.NUMBER",
                        "value": 1
                    },
                    "to": {
                        "type": "YANDEX.NUMBER",
                        "value": 6
                    }
                }
            }
        }
    },
    ...
}`}
                    </CodeBlock>
                </Paragraph>
                <Paragraph>Пример для гео-объекта и даты:</Paragraph>
                <Paragraph>
                    <CodeBlock type="json">
                        {`"request": {
    "command": "закажи такси на льва толстого 16 на 14:00",
    "nlu": {
      "intents": {
        "taxi": {
          "slots": {
            "where": {
              "type": "YANDEX.GEO",
              "tokens": {
                "start": 2,
                "end": 6
              },
              "value": {
                "street": "льва толстого",
                "house_number": "16"
              }
            },
            "time": {
              "type": "YANDEX.DATETIME",
              "tokens": {
                "start": 6,
                "end": 9
              },
              "value": {
                "hour": 14,
                "minute": 0
              }
            }
          }
        }
      }
    },
    ...
}`}
                    </CodeBlock>
                </Paragraph>
                <Paragraph>Пример для пользовательского типа:</Paragraph>
                <Paragraph>
                    <CodeBlock type="json">
                        {`"request": {
    "command": "ход ферзем",
    "nlu": {
      "tokens": [
        "ход",
        "ферзем"
      ],
      "entities": [],
      "intents": {
        "chess": {
          "slots": {
            "piece": {
              "type": "ChessPiece",
              "tokens": {
                "start": 1,
                "end": 2
              },
              "value": "queen"
            }
          }
        }
      }
    },
    ...
}`}
                    </CodeBlock>
                </Paragraph>{' '}
            </article>
        </DocPage>
    );
};
